// src/pages/TradingData/IndicatorConfigs.js
// All indicator signal configurations — import into any component

export const ASSETS = ['NQ', 'QQQ', 'GC', 'CL', 'ZN', 'ES'];
export const TIMEFRAMES = ['1m', '2m', '3m', '5m', '10m', '15m', '30m', '45m', '1h', '2h', '3h', '4h', '1D', '1W'];

export const TIMEFRAME_LABELS = {
  '1m': '1 minute', '2m': '2 minutes', '3m': '3 minutes', '5m': '5 minutes',
  '10m': '10 minutes', '15m': '15 minutes', '30m': '30 minutes', '45m': '45 minutes',
  '1h': '1 hour', '2h': '2 hours', '3h': '3 hours', '4h': '4 hours',
  '1D': '1 day', '1W': '1 week',
};
export const SESSIONS = ['ETH', 'RTH'];
export const MONO = '"JetBrains Mono", monospace';

const INDICATOR_CONFIGS = {
  LUX: {
    label: 'LUX Algo',
    accent: '#a855f7',
    hasDirection: true,
    sensitivities: { LONG: [12, 15, 18, 21], SHORT: [12, 15, 18, 21] },
    defaultSensitivity: 15,
    buildSignal: (dir, sens) => `LUX_${dir}${sens !== 15 ? `_${sens}` : ''}`,
  },
  COBRA: {
    label: 'Cobra Algo',
    accent: '#00ffaa',
    hasDirection: true,
    sensitivities: [10, 12, 13, 14, 15, 18],
    modes: ['Medium', 'Slow'],
    defaultSensitivity: 15,
    defaultMode: 'Medium',
    // Sens 15 + Medium → COBRA_LONG (base signal, no suffix)
    // Sens 15 + None → COBRA_LONG_15
    // Sens 15 + Slow → COBRA_LONG_15_SLOW
    // Sens 13 + Slow → COBRA_LONG_13_SLOW
    // Sens 12 + None → COBRA_LONG_12
    // Sens 12 + Slow → COBRA_LONG_12_SLOW
    // Sens 18 + Medium → COBRA_LONG_18_MEDIUM
    // 45s + 15 + Medium → COBRA_LONG_DEFAULT
    buildSignal: (dir, sens, mode, tf) => {
      if (tf === '45s' && sens === 15 && mode === 'Medium') return `COBRA_${dir}_DEFAULT`;
      if (sens === 15 && mode === 'Medium') return `COBRA_${dir}`;
      let s = `COBRA_${dir}_${sens}`;
      if (mode === 'Slow') s += '_SLOW';
      else if (mode === 'Medium') s += '_MEDIUM';
      return s;
    },
  },
  VGB: {
    label: 'VGB — Volatility Gaussian Band',
    accent: '#06b6d4',
    hasDirection: true,
    lengths: [20, 26, 30, 34, 35, 45, 55, 60],
    defaultLength: 20,
    types: [1, 2],
    defaultType: 1,
    // Length 20 (default) is unsuffixed; every other length appends _<length>.
    //   Length 20 + Type 1 → VGB_LONG
    //   Length 35 + Type 1 → VGB_LONG_35
    //   Length 20 + Type 2 → VGB_LONG_2
    //   Length 55 + Type 2 → VGB_LONG_2_55
    buildSignal: (dir, length, type) => {
      let s = `VGB_${dir}`;
      if (type === 2) s += '_2';
      if (length !== 20) s += `_${length}`;
      return s;
    },
  },
  SMART_BAND: {
    label: 'Smart Band — MarketWaves',
    accent: '#f59e0b',
    hasDirection: true,
    sensitivities: [12, 13, 15, 16, 17, 18],
    defaultSensitivity: 15,
    warning: 'Backend-only — no Pine Script',
    buildSignal: (dir, sens) => `SMART_BAND_${sens}${dir === 'SHORT' ? '_SHORT' : ''}`,
  },
  SMART_TRAIL: {
    label: 'Smart Trail',
    accent: '#f43f5e',
    hasDirection: true,
    versions: [2, 3],
    defaultVersion: 2,
    buildSignal: (dir, version = 2) => `SMART_TRAIL_${version}${dir === 'SHORT' ? '_SHORT' : ''}`,
  },
  EMA: {
    label: 'EMA Cross',
    accent: '#3b82f6',
    hasCrossDirection: true,
    periods: [75, 200],
    defaultPeriod: 75,
    buildSignal: (crossDir, period) =>
      period === 200 ? `EMA200_CROSS_${crossDir}_45S` : `EMA${period}_CROSS_${crossDir}`,
  },
  ALGO_PRO: {
    label: 'Algo Pro',
    accent: '#10b981',
    hasDirection: true,
    buildSignal: (dir) => `ALGO_PRO_${dir}`,
  },
  TARGET: {
    label: 'Target Trend',
    accent: '#f97316',
    hasDirection: true,
    buildSignal: (dir) => `TARGET_TREND_${dir}`,
  },
  MACD: {
    label: 'MACD',
    accent: '#818cf8',
    hasDirection: true,
    modes: ['CROSS', 'SIGNAL'],
    defaultMode: 'CROSS',
    buildSignal: (dir, mode) => {
      if (mode === 'SIGNAL') return dir === 'LONG' ? 'MACD_SIGNAL_ABOVE_2' : 'MACD_SIGNAL_BELOW_2';
      return dir === 'LONG' ? 'MACD_CROSS_ABOVE' : 'MACD_CROSS_BELOW';
    },
  },
  ALGO_ALPHA: {
    label: 'Algo Alpha',
    accent: '#f472b6',
    hasDirection: true,
    settings: ['DEFAULT', 3, 4, 8, 11],
    defaultSetting: 'DEFAULT',
    scalps: ['OFF', 1, 3, 4, 7, 11],
    defaultScalp: 'OFF',
    // SCALP=OFF → use SETTING normally (ALGO_ALPHA_<setting>_<dir>)
    // SCALP=<n> → ALGO_ALPHA_<n>_<dir>_SCALP   (e.g. ALGO_ALPHA_4_LONG_SCALP)
    buildSignal: (dir, setting = 'DEFAULT', scalp = 'OFF') => {
      if (scalp !== 'OFF') return `ALGO_ALPHA_${scalp}_${dir}_SCALP`;
      return `ALGO_ALPHA_${setting}_${dir}`;
    },
  },
  TRIDENT_SCALP: {
    label: 'Trident Scalp',
    accent: '#14b8a6',
    hasDirection: true,
    sensitivities: [10],
    defaultSensitivity: 10,
    // Sensitivity 10 + LONG → TRIDENT_SCALP_10_LONG
    buildSignal: (dir, sens = 10) => `TRIDENT_SCALP_${sens}_${dir}`,
  },
};

export default INDICATOR_CONFIGS;
